import { z } from 'zod';

import type { InstrumentKind } from '../instrument.ts';
import type { TradeType } from '../primitives.ts';
import { hasEnded, type OrderStatus } from './order.ts';
import { type RecommendationStatus, settledWithItsContract } from './recommendation.ts';

/** A demat row's shares less those the desk's legs hold in it, which count as trades. */
export const ownShares = (
  row: { tradingsymbol: string; quantity: number },
  legs: readonly { tradingsymbol: string; quantity: number }[],
): number =>
  row.quantity -
  legs
    .filter((leg) => leg.tradingsymbol === row.tradingsymbol)
    .reduce((sum, leg) => sum + leg.quantity, 0);

export const POSITION_STATUSES = ['OPEN', 'EXITED'] as const;
export type PositionStatus = (typeof POSITION_STATUSES)[number];

/**
 * Also true of a position never sent: nothing of its entry is left to ask the broker.
 * `listLiveAtBroker` reads it in SQL — change both.
 */
export function isEntrySettled(entryStatus: OrderStatus | null | undefined): boolean {
  return entryStatus == null || hasEnded(entryStatus);
}

/**
 * No entry order means a trade the desk did not place, held.
 * `HELD` in `queries/fragments.ts` is this in SQL — change both.
 */
export function isHeld(entryStatus: OrderStatus | null | undefined): boolean {
  return entryStatus == null || deskPlaced(entryStatus);
}

/** Read off the fill, not the requesting actor — an unfilled order put nothing on. */
export function deskPlaced(entryStatus: OrderStatus | null | undefined): boolean {
  return entryStatus === 'FILLED';
}

type Exiting = { entry: { status: OrderStatus } | null; exitsWithRecommendation: boolean };

export const exitsWithItsRecommendation = (position: Exiting): boolean =>
  deskPlaced(position.entry?.status) && position.exitsWithRecommendation;

/** A contract that settles cannot wait on its member, so an expiry squares off whatever they chose. */
export const squaredOffAtClose = (position: Exiting, closedAs: RecommendationStatus): boolean =>
  exitsWithItsRecommendation(position) ||
  (deskPlaced(position.entry?.status) && settledWithItsContract(closedAs));

/** One of the desk's own processes, never a person: every such actor is `system:` and a name. */
export const isSystemActor = (actor: string): boolean => actor.startsWith('system:');

export const AUTOPILOT_ACTOR = 'system:autopilot';

/** Distinct from autopilot: a different permission, and the audit must tell rolls from takes. */
export const AUTO_ROLL_ACTOR = 'system:auto-roll';

/** Rows written before this actor existed carry `AUTOPILOT_ACTOR` for the same act. */
export const DESK_ACTOR = 'system:desk';

export const BROKER_ACTOR = 'system:zerodha-orders';

/** The watcher, off the desk's tick stream. */
export const STREAM_ACTOR = 'system:zerodha-stream';

export const openedByAutopilot = (position: { openedBy: string }): boolean =>
  position.openedBy === AUTOPILOT_ACTOR;

/**
 * The desk's own square-off, not any system actor: fallback stops announce themselves, so
 * only the desk's fill note must carry the reason the recommendation ended.
 */
export const exitedByDesk = (position: { exitedBy: string | null }): boolean =>
  position.exitedBy === DESK_ACTOR || position.exitedBy === AUTOPILOT_ACTOR;

const POSITION_FILTERS = ['open', 'exited', 'all'] as const;
export type PositionFilter = (typeof POSITION_FILTERS)[number];

export interface PositionSnapshot {
  tradeType: TradeType;
  entryPrice: number;
  quantity: number;
  status: PositionStatus;
  exitPrice: number | null;
}

export function isExited(status: PositionStatus): boolean {
  return status === 'EXITED';
}

/** Liveness checks must use isOpen/isExited so a new status cannot mean different things. */
export function isOpen(status: PositionStatus): boolean {
  return status === 'OPEN';
}

export const quantitySchema = z.number().int().positive();

export function validateLeg(tradeType: TradeType, instrumentKind: InstrumentKind): string[] {
  if (tradeType !== 'SHORT') return [];
  if (instrumentKind === 'CASH') return ['a short position can only be taken on a future'];
  if (instrumentKind === 'OPTION') {
    return ['an option position is always bought — writing options is not supported'];
  }
  return [];
}

/** Same wording for someone else's position: never reveal whether it exists. */
export const NO_SUCH_POSITION = 'You have no such position.';
